import { refPort, refCountry } from "@tc/drizzle-v2"
import { eq, and, inArray } from "drizzle-orm"
import {
  SQSClient,
  SendMessageCommand,
  MessageAttributeValue,
} from "@aws-sdk/client-sqs"
import { DbTransaction } from "@tc/middy-decorators"
import { PortDoc } from "../types/reference-data"
import { searchBuilderLog } from "../utils/logger"

const sqs_client = new SQSClient({})
const queue_url = process.env["PORT_QUEUE_URL"]

export const getPortData = async (
  tx: DbTransaction,
  id: string,
): Promise<PortDoc | undefined> => {
  const port_id = Number(id)
  const [row] = await tx
    .select({
      id: refPort.id,
      countryId: refPort.countryId,
      countryCode: refCountry.itemCode,
      portCode: refPort.itemCode,
      portName: refPort.itemName,
      nzHub: refPort.nzHub,
      active: refPort.active,
      updatedAt: refPort.updatedAt,
      updatedBy: refPort.updatedBy,
    })
    .from(refPort)
    .leftJoin(refCountry, eq(refPort.countryId, refCountry.id))
    .where(and(eq(refPort.id, port_id), eq(refPort.active, true)))

  if (!row) {
    console.error(`Port with id ${id} not found/active`)
    return
  }

  const portDoc: PortDoc = {
    id: row.id.toString(),
    country_id: row.countryId,
    country_code: row.countryCode ?? null,
    port_code: row.portCode,
    port_name: row.portName ?? null,
    nz_hub: row.nzHub ?? null,
    active: row.active,
    updated_at: row.updatedAt.getTime(),
    updated_by: row.updatedBy ?? null,
  }

  searchBuilderLog.debug({
    document: portDoc,
  })

  return portDoc
}

export const getPortBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, PortDoc>> => {
  if (ids.length === 0) return new Map()

  const portIds = ids.map(Number)
  const rows = await tx
    .select({
      id: refPort.id,
      countryId: refPort.countryId,
      countryCode: refCountry.itemCode,
      portCode: refPort.itemCode,
      portName: refPort.itemName,
      nzHub: refPort.nzHub,
      active: refPort.active,
      updatedAt: refPort.updatedAt,
      updatedBy: refPort.updatedBy,
    })
    .from(refPort)
    .leftJoin(refCountry, eq(refPort.countryId, refCountry.id))
    .where(and(inArray(refPort.id, portIds), eq(refPort.active, true)))

  const result = new Map<string, PortDoc>()
  for (const row of rows) {
    result.set(row.id.toString(), {
      id: row.id.toString(),
      country_id: row.countryId,
      country_code: row.countryCode ?? null,
      port_code: row.portCode,
      port_name: row.portName ?? null,
      nz_hub: row.nzHub ?? null,
      active: row.active,
      updated_at: row.updatedAt.getTime(),
      updated_by: row.updatedBy ?? null,
    })
  }
  return result
}

export const savePortToSearch = async (
  tx: DbTransaction,
  id: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getPortData(tx, id)

  if (!doc || Object.keys(doc).length === 0) {
    console.error(`Failed to prepare document for id: ${id}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }

  const command = new SendMessageCommand({
    QueueUrl: queue_url,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqs_client.send(command)
}
